import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient, UseMutationResult } from '@tanstack/react-query'

import { ApiResponse } from '@/api/services/CustomerService'
import { KycService } from '@/api/services/KycService'
import { useAppStore } from '@/store'
import { IKyc } from '@/types'

export type RefreshKycMutation = UseMutationResult<void, Error, string>

const errorMessage = (error: unknown): string => {
  const err = error as { response?: { data?: { message?: string } }; message?: string }
  return err?.response?.data?.message ?? err?.message ?? 'Failed to refresh KYC. Please try again.'
}

export const useKyc = () => {
  const { user, kycPage, setKycPage } = useAppStore()
  const userId = user?.id
  const queryClient = useQueryClient()
  const [toastError, setToastError] = useState<string | null>(null)

  // Avoid carrying pagination state across customers.
  useEffect(() => {
    setKycPage(1)
  }, [userId])

  const query = useQuery<ApiResponse<IKyc>>({
    // userId is part of the key so a different customer never reads cached KYC data.
    queryKey: ['kyc', userId, kycPage],
    queryFn: () => KycService.fetchKycs(kycPage),
    enabled: !!userId,
    placeholderData: prev => prev,
  })

  const refresh: RefreshKycMutation = useMutation({
    mutationFn: (kycId: string) => KycService.refreshKyc(kycId),
    // Refetch the list so the refreshed record reflects its new status.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['kyc', userId] }),
    onError: error => setToastError(errorMessage(error)),
  })

  // Refresh only the latest KYC, and only when it's stuck (Failed/InProcessing).
  // List is newest-first, but pick by createdAt so we don't depend on order
  const kycs = query.data?.data ?? []
  const latest = kycs.length
    ? kycs.reduce((a, b) => ((b.createdAt ?? '') > (a.createdAt ?? '') ? b : a))
    : undefined
  const refreshableKycId =
    latest && (latest.status === 'PENDING' || latest.status === 'FAILED')
      ? String(latest.kycId)
      : undefined

  return {
    userId,
    kycPage,
    setKycPage,
    query,
    refresh,
    refreshableKycId,
    toastError,
    clearToastError: () => setToastError(null),
  }
}
